import { Controller } from "@hotwired/stimulus"
import PouchDB from "pouchdb-browser"
import { renderOfflineRecipe } from "../helpers/offline_recipe_renderer"

// Module-level cache of the per-household PouchDB + in-flight replication.
// Keeping these here (NOT on the controller instance) means they survive
// Turbo navigation: connect() reattaches refs without re-instantiating
// PouchDB or restarting replication. Every Turbo morph used to fire a
// fresh /sync/_changes request — with max_threads = 3 in dev, three
// stacked navs would starve every other request (8s TTFB on images).
let cachedHouseholdId = null
let cachedRemote = null
let cachedLocal = null
let cachedReplication = null

// Reset hook for vitest. Module-level state otherwise leaks across tests
// (PouchDB constructor never re-called after the first test). Not used
// in production code paths.
export function __resetCacheForTesting() {
  try { cachedReplication?.cancel?.() } catch { /* mock or already-cancelled */ }
  cachedHouseholdId = null
  cachedRemote = null
  cachedLocal = null
  cachedReplication = null
}

// Boots a per-household PouchDB on first connect and starts replication
// from the server's CouchDB through Rails' /sync/<db_name> proxy.
//
// Stays dormant when couch_enabled? is false on the server (the
// enabled-value is rendered from Recidex.config.couch_enabled?), or
// when the current user isn't in a household yet — both of those
// states are normal and shouldn't surface as errors.
export default class extends Controller {
  static values = {
    enabled: { type: Boolean, default: false },
    householdId: { type: String, default: "" }
  }
  static targets = ["offlineBanner", "offlineModal", "offlineModalContent"]

  connect() {
    this.#syncBanner()
    if (!this.enabledValue || !this.householdIdValue) return
    this.#startReplication()
  }

  disconnect() {
    // Don't cancel — the replication is module-level and survives Turbo
    // morphs. Cancelling here would force a restart on every nav, which
    // saturates Puma threads (the original 8s-TTFB-on-images bug).
  }

  // window event handlers (wired via data-action on body)
  onOnline() { this.#syncBanner() }
  onOffline() { this.#syncBanner() }

  // Used by other controllers via Stimulus outlets to read cached docs.
  // Returns null when the local DB isn't initialized (couch disabled, or
  // the user isn't in a household yet) or when the doc isn't present.
  async findDoc(docId) {
    if (!this.local) return null
    try {
      return await this.local.get(docId)
    } catch (err) {
      if (err.status === 404) return null
      throw err
    }
  }

  // turbo:before-visit handler. When the user is offline AND the
  // requested URL is /recipes/N AND that recipe is cached locally,
  // we cancel the navigation and pop a modal showing the offline copy.
  // When any of those conditions miss, we let Turbo navigate (and fail
  // gracefully via the offline banner / its own error handling).
  async handleOfflineNavigation(event) {
    if (navigator.onLine) return
    const match = (event.detail?.url || "").match(/\/recipes\/(\d+)(?:\?|#|$)/)
    if (!match) return

    const doc = await this.findDoc(`recipe-${match[1]}`)
    if (!doc) return

    event.preventDefault()
    this.#showOfflineRecipeModal(doc)
  }

  hideOfflineModal() {
    if (!this.hasOfflineModalTarget) return
    this.offlineModalTarget.classList.add("hidden")
  }

  // Wired on the modal's inner card so backdrop clicks don't fall through
  // to the outer hideOfflineModal handler.
  stopPropagation(event) {
    event.stopPropagation()
  }

  #showOfflineRecipeModal(doc) {
    if (!this.hasOfflineModalTarget || !this.hasOfflineModalContentTarget) return
    this.offlineModalContentTarget.innerHTML = renderOfflineRecipe(doc)
    this.offlineModalTarget.classList.remove("hidden")
  }

  #syncBanner() {
    if (!this.hasOfflineBannerTarget) return
    this.offlineBannerTarget.classList.toggle("hidden", navigator.onLine)
  }

  #startReplication() {
    const householdId = this.householdIdValue

    // Reuse the module-level singletons when the household hasn't changed.
    // This is the key perf fix: Turbo morphs reconnect this controller on
    // every nav, but we MUST NOT restart replication each time — that
    // saturates Puma threads.
    if (cachedHouseholdId === householdId && cachedLocal) {
      this.remote = cachedRemote
      this.local = cachedLocal
      this.replication = cachedReplication
      return
    }

    // Different household (logout / switch user) — cancel the old replication
    // before starting fresh.
    if (cachedReplication) cachedReplication.cancel()

    // Remote URL MUST be absolute. PouchDB treats any name not starting
    // with http(s):// as a local IndexedDB name, so passing "/sync/foo"
    // silently creates a second local DB and "replicates" between two
    // empty IDBs. Browser fetch attaches same-origin session cookies
    // automatically — Sync::DatabasesController auths on those.
    cachedRemote = new PouchDB(`${window.location.origin}/sync/household-${householdId}`, { skip_setup: true })
    cachedLocal = new PouchDB(`recidex-household-${householdId}`)
    cachedHouseholdId = householdId

    // NOT `live: true` — that issues a longpoll _changes request that
    // holds a Puma thread for `heartbeat` ms (10s default). Module-level
    // singleton means one-shot replication runs ONCE per session; the
    // user gets fresh data on every page load via server render anyway.
    //
    // `heartbeat: false` + `timeout: 5000` are belt-and-suspenders: even
    // on a one-shot replication, PouchDB's internal `_changes` discovery
    // step defaults to `feed=longpoll&heartbeat=10000`, which still pins
    // the proxied Puma thread for ~10s per round. With only 3 threads in
    // production, that was 33% of capacity gone whenever sync was warm —
    // and on iOS cookbook grids (40 concurrent image fetches) it starved
    // the pool so other households' avatars / recipe photos timed out
    // into broken-image icons. Forcing no-heartbeat + a 5s upstream
    // timeout makes _changes return immediately, freeing the thread.
    cachedReplication = cachedLocal.replicate.from(cachedRemote, {
      retry: true,
      heartbeat: false,
      timeout: 5000
    })
      .on("error", err => console.warn("LocalData sync error:", err))

    this.remote = cachedRemote
    this.local = cachedLocal
    this.replication = cachedReplication
  }
}
